// src/App.js
// -----------------------------------------------------------------------------
// Shell principal : applique le thème, monte la Sidenav et les Routes.
// Corrections:
//  - La sidebar `routes={visibleRoutes}` n'est calculée qu'une fois `user` chargé,
//    pour éviter tout flash/état incohérent.
//  - (Rappel) Les pages privées sont protégées par <PrivateRoute> puis <RequireRole>.
//  - Ajout de deux routes PUBLIQUES QuickBooks : /quickbooks/launch et /quickbooks/disconnected
//    * Launch : démarre le flux OAuth en appelant le backend /quickbooks/connect/
//    * Disconnected : simple page d'atterrissage après révocation (visuelle)
// -----------------------------------------------------------------------------

import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "routes/routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator, setLayout } from "context";

import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import PrivateRoute from "components/PrivateRoute";
import RequireRole from "components/RequireRole";
import { useAuth } from "context/AuthContext";
import { filterRoutesForUser } from "routes/filterRoutesForUser";

// 🔹 IMPORTS DES PAGES PUBLIQUES QUICKBOOKS (sous layouts/)
import Launch from "layouts/quickbooks/Launch";
import Disconnected from "layouts/quickbooks/Disconnected";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const { isAuthenticated, isLoading, user } = useAuth();
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // 🌐 Les pages qui utilisent le layout "dashboard" (Sidenav & co)
  const dashboardPaths = [
    "/dashboard",
    "/all-sales",
    "/expenditures",
    "/bills",
    "/vendors",
    "/reports",
    "/forecasts",
    "/budgets",
    "/billing",
    "/clients",
    "/add-user",
    "/users",
    "/products-services",
    "/pledges",
    "/estimates",
    "/sales-orders",
    "/tables",
    "/profile",
    "/notifications",
    "/teams",
  ];

  useEffect(() => {
    const isDashboardLayout = dashboardPaths.some((path) => pathname.startsWith(path));
    setLayout(dispatch, isDashboardLayout ? "dashboard" : "");
  }, [pathname, dispatch]);

  useMemo(() => {
    const cacheRtl = createCache({ key: "rtl", stylisPlugins: [rtlPlugin] });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // ⏱️ Auto-logout si token expiré (logique existante conservée)
  useEffect(() => {
    const expiresAt = localStorage.getItem("expires_at");
    const now = Date.now();
    if (expiresAt && now > parseInt(expiresAt, 10)) {
      localStorage.clear();
      window.location.href = "/authentication/sign-in";
    }
  }, []);

  // ✅ On ne filtre le menu par rôle QUE quand user est connu (évite états transitoires)
  const visibleRoutes = useMemo(() => {
    if (!user) return []; // tant que l'on ne sait pas, on n'affiche pas le menu
    return filterRoutesForUser(routes, user.role);
  }, [user]);

  // Helper de génération des <Route/> (inchangé)
  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) return getRoutes(route.collapse);

      if (route.route) {
        const isPublic =
          route.route === "/authentication/sign-in" ||
          route.route === "/authentication/sign-up" ||
          // ⚠️ Les routes légales/support sont déclarées dans routes.js comme publiques
          route.route === "/legal/terms" ||
          route.route === "/legal/privacy" ||
          route.route === "/legal/eula" ||
          route.route === "/support";

        if (!route.component) return [];

        if (isPublic) {
          return [
            <Route
              exact
              path={route.route}
              element={route.component}
              key={route.key || route.route}
            />,
          ];
        }

        const element = route.allowedRoles ? (
          <RequireRole allowedRoles={route.allowedRoles}>{route.component}</RequireRole>
        ) : (
          route.component
        );

        return [
          <Route element={<PrivateRoute />} key={`wrap-${route.key || route.route}`}>
            <Route exact path={route.route} element={element} />
          </Route>,
        ];
      }
      return [];
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const AppShell = (
    <>
      {layout === "dashboard" && (
        <>
          {/* ⚠️ Tant que `user` n'est pas prêt, on masque la Sidenav pour éviter des clicks vers des pages potentiellement non autorisées */}
          {user && (
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Material Dashboard 2"
              routes={visibleRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          <Configurator />
          {configsButton}
        </>
      )}

      <Routes>
        {/* Accueil : redirige vers dashboard si authentifié, sinon login */}
        <Route
          path="/"
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} replace />
          }
        />

        {/* 🔓 ROUTES PUBLIQUES QUICKBOOKS (demandées par Intuit) */}
        {/* Launch → démarre le flux OAuth côté backend (/quickbooks/connect/) */}
        <Route path="/quickbooks/launch" element={<Launch />} />
        {/* Disconnected → simple page d'info après révocation / déconnexion */}
        <Route path="/quickbooks/disconnected" element={<Disconnected />} />

        {/* Routes générées depuis ta config `routes` (privées & publiques auth) */}
        {getRoutes(routes)}

        {/* Catch-all */}
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} replace />
          }
        />
      </Routes>
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {AppShell}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {AppShell}
    </ThemeProvider>
  );
}
